import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import TriangleDiagram from "../../components/diagrams/TriangleDiagram";
import TrigValuesTable from "../../components/TrigValuesTable";

function Triangle345() {
  return (
    <TriangleDiagram
      a={4}
      b={3}
      c={5}
      angleAt="B"
      labelA="4"
      labelB="3"
      labelC="5"
      vertexLabels={{ A: "C", B: "A", C: "B" }}
    />
  );
}

export default function AcuteAngleTrigPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        W trójkącie prostokątnym najpierw wskazujemy przeciwprostokątną oraz dwie
        przyprostokątne względem wybranego kąta ostrego <InlineMath math="\alpha" />.
        Na standardowym rysunku z karty wzorów bok <InlineMath math="a" /> leży naprzeciw
        kąta, bok <InlineMath math="b" /> jest do niego przyległy, a <InlineMath math="c" />
        jest przeciwprostokątną.
      </p>
      <TriangleDiagram
        a={4}
        b={3}
        c={5}
        angleAt="B"
        labelA="b"
        labelB="a"
        labelC="c"
        vertexLabels={{ A: "C", B: "A", C: "B" }}
      />
      <BlockMath math="\sin\alpha=\frac{a}{c}=\frac{\text{przyprostokątna naprzeciw }\alpha}{\text{przeciwprostokątna}}" />
      <BlockMath math="\cos\alpha=\frac{b}{c}=\frac{\text{przyprostokątna przyległa do }\alpha}{\text{przeciwprostokątna}}" />
      <BlockMath math="\tan\alpha=\frac{a}{b}=\frac{\text{przyprostokątna naprzeciw }\alpha}{\text{przyprostokątna przyległa do }\alpha}" />
      <p>
        Jeżeli nie znamy przeciwprostokątnej, obliczamy ją z twierdzenia Pitagorasa.
        W części ćwiczeń pojawi się wtedy wynik z pierwiastkiem.
      </p>

      <h3>Wartości dla kątów szczególnych</h3>
      <p>
        Na karcie wzorów znajduje się tabela wartości funkcji trygonometrycznych dla kątów{" "}
        <InlineMath math="0^\circ" />, <InlineMath math="30^\circ" />,{" "}
        <InlineMath math="45^\circ" />, <InlineMath math="60^\circ" /> i{" "}
        <InlineMath math="90^\circ" />. Najpierw wybieramy wiersz funkcji, na przykład{" "}
        <InlineMath math="\sin\alpha" />, a następnie kolumnę z kątem. Szukana wartość leży
        na przecięciu wybranego wiersza i kolumny.
      </p>
      <TrigValuesTable />
      <p>
        Przykładowo w wierszu <InlineMath math="\cos\alpha" /> i kolumnie{" "}
        <InlineMath math="60^\circ" /> odczytujemy <InlineMath math="\frac{1}{2}" />.
        Zwróć uwagę, że <InlineMath math="\tan 90^\circ" /> nie istnieje.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: (
        <>
          Obliczanie sinusa w trójkącie <InlineMath math="3\text{–}4\text{–}5" />
        </>
      ),
      description: (
        <>
          Naprzeciw kąta <InlineMath math="\alpha" /> leży bok długości{" "}
          <InlineMath math="3" />, a przeciwprostokątna ma długość{" "}
          <InlineMath math="5" />.
        </>
      ),
      content: (
        <div className="space-y-2">
          <Triangle345 />
          <BlockMath math="\sin\alpha=\frac{3}{5}" />
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: (
        <>
          Obliczanie cosinusa w trójkącie <InlineMath math="3\text{–}4\text{–}5" />
        </>
      ),
      description: (
        <>
          Do kąta <InlineMath math="\alpha" /> przylega przyprostokątna długości{" "}
          <InlineMath math="4" />, a przeciwprostokątna ma długość{" "}
          <InlineMath math="5" />.
        </>
      ),
      content: (
        <div className="space-y-2">
          <Triangle345 />
          <BlockMath math="\cos\alpha=\frac{4}{5}" />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: (
        <>
          Odczytywanie tangensa kąta <InlineMath math="\alpha" /> z boków trójkąta
        </>
      ),
      description: (
        <>
          Dzielimy długość przyprostokątnej naprzeciw kąta <InlineMath math="\alpha" /> przez
          długość przyprostokątnej przyległej.
        </>
      ),
      content: (
        <div className="space-y-2">
          <Triangle345 />
          <BlockMath math="\tan\alpha=\frac{3}{4}" />
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Odczytywanie wartości z tabeli na karcie wzorów",
      description: (
        <>
          Obliczamy <InlineMath math="\cos 60^\circ" />. Wybieramy wiersz{" "}
          <InlineMath math="\cos\alpha" /> i kolumnę <InlineMath math="60^\circ" />.
        </>
      ),
      content: (
        <div className="space-y-2">
          <TrigValuesTable highlightFunc="cos" highlightAngle={60} />
          <BlockMath math="\cos 60^\circ=\frac{1}{2}" />
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
